/**
 * Product Kit Builder
 * Gerencia a interface de montagem de kits personalizáveis
 */

class ProductKitBuilder {
  constructor(container, config) {
    this.container = container;
    this.config = config;
    this.currentSlide = 0;
    this.selections = new Map(); // key: `${productId}-${rep}` -> { productId, variantId }
    
    // Elementos DOM
    this.slider = container.querySelector('[data-kit-slider]');
    this.slides = Array.from(this.slider.querySelectorAll('.kit-product-slide'));
    this.prevButton = container.querySelector('[data-kit-prev]');
    this.nextButton = container.querySelector('[data-kit-next]');
    this.addButton = container.querySelector('[data-kit-add]');
    this.currentCounter = container.querySelector('[data-kit-current]');
    this.totalCounter = container.querySelector('[data-kit-total]');
    
    this.init();
  }

  init() {
    // Inicializar seleções para todas as instâncias de slides
    const allSlides = Array.from(this.container.querySelectorAll('.kit-product-slide'));
    allSlides.forEach((slide) => {
      const productId = parseInt(slide.dataset.productId);
      const rep = parseInt(slide.dataset.rep || '1');
      const productData = this.config.products.find((p) => p.id === productId);
      const key = `${productId}-${rep}`;
      if (productData) {
        this.selections.set(key, {
          productId,
          variantId: productData.firstAvailableVariantId
        });
      }
    });

    console.log('🎬 Kit Builder inicializado:', {
      totalProducts: this.config.products.length,
      selectedVariants: this.selectedVariants
    });

    // Aplicar tamanho de kit padrão (ocultar excedentes já vem do HTML)
    // Usar o primeiro valor da lista configurada (não forçar 1)
    const defaultSize = this.config.kitDefaultSize || (this.config.kitSizes && this.config.kitSizes.length > 0 ? parseInt(this.config.kitSizes[0]) : 2);
    this.applyKitSize(defaultSize);

    this.setupEventListeners();
    this.showSlide(0);
  }

  setupEventListeners() {
    // Botões de navegação
    this.prevButton.addEventListener('click', () => this.previousSlide());
    this.nextButton.addEventListener('click', () => this.nextSlide());
    this.addButton.addEventListener('click', () => this.addToCart());

    // Radio buttons de variantes (delegação)
    this.container.addEventListener('change', (e) => {
      const radio = e.target.closest('.kit-variant-radio');
      if (radio) this.handleVariantChange(e);
    });

    // Navegação por teclado
    this.container.addEventListener('keydown', (e) => this.handleKeyboard(e));

    // Seletor de tamanhos do kit
    const sizeButtons = this.container.querySelectorAll('[data-kit-size]');
    sizeButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const size = parseInt(btn.dataset.kitSize);
        this.applyKitSize(size);

        // Atualiza estados visuais dos botões
        sizeButtons.forEach((b) => {
          const pressed = parseInt(b.dataset.kitSize) === size;
          b.setAttribute('aria-pressed', pressed ? 'true' : 'false');
          b.classList.toggle('is-active', pressed);
        });

        // Subtextos permanecem sempre visíveis por opção
      });
    });
  }

  getVisibleSlides() {
    return Array.from(this.slider.querySelectorAll('.kit-product-slide:not([data-hidden="true"])'));
  }

  applyKitSize(size) {
    // Oculta/mostra slides por repetição
    const allSlides = Array.from(this.slider.querySelectorAll('.kit-product-slide'));
    allSlides.forEach((slide) => {
      const rep = parseInt(slide.dataset.rep || '1');
      if (rep > size) {
        slide.style.display = 'none';
        slide.setAttribute('data-hidden', 'true');
      } else {
        slide.style.display = '';
        slide.removeAttribute('data-hidden');
      }
    });

    // Atualiza lista interna e contador
    this.slides = this.getVisibleSlides();
    if (this.totalCounter) {
      this.totalCounter.textContent = this.slides.length;
    }
    this.currentSlide = 0;
    this.showSlide(0);

    // Atualiza desconto por tamanho (se configurado)
    const discountPercent = (this.config.kitDiscounts && this.config.kitDiscounts[String(size)]) || 0;
    const discountFixed = (this.config.kitDiscountsFixed && this.config.kitDiscountsFixed[String(size)]) || 0;
    
    // Priorizar desconto fixo por tamanho, depois porcentagem por tamanho, depois desconto geral
    if (discountFixed > 0) {
      // Desconto fixo será aplicado proporcionalmente por produto
      this.config.discountMultiplier = 1.0;
      this.config.discountFixedCents = discountFixed;
      this.config.discountType = 'fixed';
    } else if (discountPercent > 0) {
      this.config.discountMultiplier = (100 - discountPercent) / 100;
      this.config.discountFixedCents = 0;
      this.config.discountType = 'percent';
    } else if (this.config.discountFixedCents > 0) {
      // Desconto fixo geral
      this.config.discountMultiplier = 1.0;
      this.config.discountType = 'fixed';
    } else {
      this.config.discountMultiplier = this.config.discountMultiplier || 1;
      this.config.discountFixedCents = 0;
      this.config.discountType = 'none';
    }

    // Recalcular preços dos slides visíveis
    const visibleSlides = this.getVisibleSlides();
    this.slides.forEach((slide) => {
      const productId = parseInt(slide.dataset.productId);
      const rep = parseInt(slide.dataset.rep || '1');
      const key = `${productId}-${rep}`;
      const selection = this.selections.get(key);
      const productData = this.config.products.find((p) => p.id === productId);
      let variant = undefined;
      if (selection) {
        variant = productData?.variants.find((v) => v.id === selection.variantId) || productData?.variants[0];
      } else {
        variant = productData?.variants[0];
      }
      if (variant) {
        this.updateProductPrice(slide, variant, visibleSlides.length);
      }
    });
  }

  handleVariantChange(event) {
    const radio = event.target;
    if (!radio.checked) return;

    const slide = radio.closest('.kit-product-slide');
    const productIndex = parseInt(slide.dataset.productIndex) - 1;
    const productId = parseInt(slide.dataset.productId);
    const rep = parseInt(slide.dataset.rep || '1');
    const fieldset = radio.closest('.kit-variant-fieldset');
    
    // Atualizar valor atual exibido
    const currentValueSpan = fieldset.querySelector('.current-value');
    if (currentValueSpan) {
      currentValueSpan.textContent = radio.value;
      currentValueSpan.dataset.currentValue = radio.value;
    }

    // Obter todas as opções selecionadas
    const selectedOptions = Array.from(
      slide.querySelectorAll('.current-value')
    ).map((el) => el.dataset.currentValue);

    console.log('🔍 Opções selecionadas:', selectedOptions);

    // Encontrar produto e variante correspondente
    const product = this.config.products[productIndex];
    const variant = product.variants.find((v) => {
      return JSON.stringify(v.options) === JSON.stringify(selectedOptions);
    });

    if (variant) {
      console.log('✅ Variante encontrada:', variant);
      
      // Atualizar seleção específica desta instância
      const key = `${productId}-${rep}`;
      this.selections.set(key, {
        productId,
        variantId: variant.id
      });

      // Atualizar imagem
      this.updateProductImage(slide, variant);

      // Atualizar preço
      const visibleSlides = this.getVisibleSlides();
      this.updateProductPrice(slide, variant, visibleSlides.length);

      // Atualizar disponibilidade dos botões
      this.updateNavigationAvailability(slide, variant);

      // Atualizar estado dos swatches
      this.updateSwatchAvailability(slide, product, selectedOptions);
    } else {
      console.error('❌ Variante não encontrada para:', selectedOptions);
    }
  }

  updateProductImage(slide, variant) {
    console.log('🖼️ updateProductImage chamado:', variant);
    
    const image = slide.querySelector('[data-kit-image]');
    if (!image) {
      console.warn('⚠️ Imagem do slide não encontrada');
      return;
    }

    if (variant.featured_image) {
      image.src = variant.featured_image;
      image.alt = variant.options.join(' - ');
    }

    // Atualizar galeria com as imagens deste produto específico
    const slideProductId = parseInt(slide.dataset.productId);
    const productData = this.config.products.find(p => p.id === slideProductId);
    
    console.log('📦 Produto do slide:', productData);
    console.log('🎨 Todas as opções da variante:', variant.options);
    
    // Encontrar a opção de cor (segunda opção = index 1)
    const colorValue = variant.options[1] || variant.options[0];
    console.log('🎨 Cor selecionada (opção 2):', colorValue);
    
    if (productData && colorValue) {
      // Buscar as imagens do produto via sua URL
      console.log('🔄 Chamando updateGalleryWithProductImages...');
      this.updateGalleryWithProductImages(productData.handle, colorValue);
    } else {
      console.warn('⚠️ ProductData ou cor não disponível');
    }
  }

  updateProductPrice(slide, variant, totalProducts = 1) {
    const priceElement = slide.querySelector('[data-kit-price]');
    if (!priceElement) return;

    let discountedPrice = variant.price;
    let hasDiscount = false;

    // Aplicar desconto conforme tipo
    if (this.config.discountType === 'fixed' && this.config.discountFixedCents > 0) {
      // Desconto fixo: dividir proporcionalmente por produto
      const discountPerProduct = Math.floor(this.config.discountFixedCents / totalProducts);
      discountedPrice = Math.max(0, variant.price - discountPerProduct);
      hasDiscount = discountPerProduct > 0;
    } else if (this.config.discountType === 'percent' && this.config.discountMultiplier < 1) {
      // Desconto por porcentagem
      discountedPrice = variant.price * this.config.discountMultiplier;
      hasDiscount = true;
    } else if (this.config.discountType === 'fixed' && this.config.discountFixedCents > 0) {
      // Desconto fixo geral
      const discountPerProduct = Math.floor(this.config.discountFixedCents / totalProducts);
      discountedPrice = Math.max(0, variant.price - discountPerProduct);
      hasDiscount = discountPerProduct > 0;
    }

    priceElement.textContent = this.formatMoney(discountedPrice);

    // Mostrar preço cortado se houver desconto
    const compareElement = slide.querySelector('[data-kit-compare-price]');
    if (compareElement) {
      if (hasDiscount) {
        compareElement.textContent = this.formatMoney(variant.price);
        compareElement.style.display = '';
      } else {
        compareElement.style.display = 'none';
      }
    }
  }

  updateNavigationAvailability(slide, variant) {
    // Se a variante não está disponível, desabilitar navegação
    if (!variant.available) {
      this.nextButton.disabled = true;
      this.addButton.disabled = true;
    } else {
      this.nextButton.disabled = false;
      this.addButton.disabled = false;
    }
  }

  updateSwatchAvailability(slide, product, selectedOptions) {
    const fieldsets = slide.querySelectorAll('.kit-variant-fieldset');
    
    fieldsets.forEach((fieldset, index) => {
      // Pular a primeira opção (geralmente cor)
      if (index === 0 && fieldsets.length > 1) return;

      const radioButtons = fieldset.querySelectorAll('input[type="radio"]');
      const previousOption = index > 0 ? selectedOptions[index - 1] : null;

      // Filtrar variantes relevantes
      const relevantVariants = previousOption
        ? product.variants.filter((v) => v.options[index - 1] === previousOption)
        : product.variants;

      radioButtons.forEach((radio) => {
        const label = radio.nextElementSibling;
        const value = radio.value;
        
        // Verificar se esta opção está disponível
        const isAvailable = relevantVariants.some(
          (v) => v.options[index] === value && v.available
        );

        if (isAvailable) {
          radio.disabled = false;
          label.classList.add('available');
          label.classList.remove('soldout');
        } else {
          radio.disabled = true;
          label.classList.add('soldout');
          label.classList.remove('available');
        }
      });
    });
  }

  showSlide(index) {
    // Esconder todos os slides visíveis
    this.slides.forEach((slide) => {
      slide.style.display = 'none';
    });

    // Mostrar slide atual
    if (this.slides[index]) {
      this.slides[index].style.display = 'block';
      this.currentSlide = index;
      
      // Atualizar contador
      if (this.currentCounter) {
        this.currentCounter.textContent = index + 1;
      }
      
      this.updateNavigation();
      
      console.log(`📍 Navegando para slide ${index + 1} de ${this.slides.length}`);
      
      // Atualizar galeria com as imagens deste produto
      this.updateGalleryForCurrentSlide(this.slides[index]);
    }
  }

  /**
   * Atualiza galeria para o slide atual
   * @param {HTMLElement} slide - Slide atual
   */
  updateGalleryForCurrentSlide(slide) {
    const productId = parseInt(slide.dataset.productId);
    const productData = this.config.products.find(p => p.id === productId);
    
    if (!productData) {
      console.warn('⚠️ Produto não encontrado para o slide');
      return;
    }
    
    // Obter TODAS as opções selecionadas
    const currentValues = slide.querySelectorAll('.current-value');
    const allOptions = Array.from(currentValues).map(el => el.dataset.currentValue);
    
    console.log('📊 Todas as opções selecionadas:', allOptions);
    
    // A cor é a segunda opção (index 1)
    const selectedColor = allOptions[1] || allOptions[0] || null;
    
    console.log('🔄 Atualizando galeria para:', productData.title, 'cor:', selectedColor);
    
    // Atualizar galeria com imagens deste produto
    if (selectedColor) {
      this.updateGalleryWithProductImages(productData.handle, selectedColor);
    } else {
      // Se não tem cor selecionada, carregar todas as imagens do produto
      this.updateGalleryWithProductImages(productData.handle, '');
    }
  }

  previousSlide() {
    if (this.currentSlide > 0) {
      this.showSlide(this.currentSlide - 1);
    }
  }

  nextSlide() {
    if (this.currentSlide < this.slides.length - 1) {
      this.showSlide(this.currentSlide + 1);
    }
  }

  updateNavigation() {
    // Desabilitar botão anterior no primeiro slide
    this.prevButton.disabled = this.currentSlide === 0;

    // No último slide, mostrar botão de adicionar ao carrinho
    const isLastSlide = this.currentSlide === this.slides.length - 1;
    
    if (isLastSlide) {
      this.nextButton.style.display = 'none';
      this.addButton.style.display = 'block';
    } else {
      this.nextButton.style.display = 'block';
      this.addButton.style.display = 'none';
    }
  }

  handleKeyboard(event) {
    switch (event.key) {
      case 'ArrowLeft':
        if (this.currentSlide > 0) {
          event.preventDefault();
          this.previousSlide();
        }
        break;
      case 'ArrowRight':
        if (this.currentSlide < this.slides.length - 1) {
          event.preventDefault();
          this.nextSlide();
        }
        break;
    }
  }

  async addToCart() {
    console.log('🛒 Adicionando kit ao carrinho...');
    console.log('📦 Variantes selecionadas:', this.selectedVariants);

    // Desabilitar botão durante o processo
    this.addButton.disabled = true;
    this.addButton.setAttribute('aria-busy', 'true');

    // Coletar itens apenas dos slides visíveis (tamanho selecionado)
    const visibleSlides = this.getVisibleSlides();
    const items = [];
    visibleSlides.forEach((slide) => {
      const productId = parseInt(slide.dataset.productId);
      const rep = parseInt(slide.dataset.rep || '1');
      const key = `${productId}-${rep}`;
      const selection = this.selections.get(key);
      if (selection && selection.variantId) {
        items.push({ id: selection.variantId, quantity: 1 });
      }
    });

    const formData = { items };

    console.log('📤 Enviando para o carrinho:', formData);

    try {
      // Preparar sections para atualização do drawer (alinha com buy-buttons do tema)
      let sectionsToBundle = ["variant-added"]; // seção padrão usada pelo tema

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        const cartDrawerSection = cartDrawer.closest('.shopify-section');
        if (cartDrawerSection) {
          sectionsToBundle.push(cartDrawerSection.id.replace('shopify-section-', ''));
        }
      }

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        })
      );

      // Incluir também a seção do ícone do carrinho (contagem) se disponível
      const cartActivator = document.querySelector('.cart-drawer-button, [aria-controls="CartDrawer"]');
      if (cartActivator) {
        const headerSection = cartActivator.closest('.shopify-section');
        if (headerSection) {
          const headerId = headerSection.id.replace('shopify-section-', '');
          if (!sectionsToBundle.includes(headerId)) sectionsToBundle.push(headerId);
        }
      }

      formData.sections = sectionsToBundle.join(',');
      formData.sections_url = window.location.pathname;

      // Adicionar ao carrinho
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          ...formData,
          // Enviar sections como array para compatibilidade máxima
          sections: sectionsToBundle,
        })
      });

      const responseJson = await response.json();

      if (response.ok) {
        console.log('✅ Kit adicionado com sucesso!', responseJson);

        // Verificar se deve redirecionar para a página do carrinho
        if (window.themeVariables?.settings?.cartType === 'page') {
          window.location.href = `${window.Shopify.routes.root}cart`;
          return;
        }

        // Buscar conteúdo atualizado do carrinho e anexar sections renderizadas
        const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
        const cartContent = await cartResponse.json();
        cartContent.sections = responseJson.sections;

        console.log('📦 Carrinho atualizado:', cartContent);

        // Publicar evento do tema para atualizar o carrinho e UI
        if (window.theme?.pubsub?.publish && window.theme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
          window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartContent });
        }

        // Abrir cart drawer usando o elemento customizado, se presente
        const cartDrawerEl = document.querySelector('cart-drawer');
        if (cartDrawerEl && typeof cartDrawerEl.show === 'function') {
          cartDrawerEl.show();
        } else {
          // Fallback: simular clique no botão do cabeçalho
          const cartDrawerButton = document.querySelector('.cart-drawer-button, [aria-controls="CartDrawer"]');
          if (cartDrawerButton) {
            setTimeout(() => cartDrawerButton.click(), 100);
          }
        }

        console.log('✅ Eventos disparados com sucesso!');
      } else {
        // Disparar evento de erro
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:error', {
            bubbles: true,
            detail: {
              error: responseJson['description']
            }
          })
        );
        console.error('❌ Erro ao adicionar:', responseJson['description']);
        alert(`Erro: ${responseJson['description']}`);
      }
    } catch (error) {
      console.error('❌ Erro ao adicionar ao carrinho:', error);
      alert('Erro ao adicionar produtos ao carrinho. Por favor, tente novamente.');
    } finally {
      // Reabilitar botão
      this.addButton.disabled = false;
      this.addButton.removeAttribute('aria-busy');
    }
  }

  /**
   * Atualiza galeria principal com imagens de um produto específico do kit
   * Busca via API do Shopify e filtra por cor
   * @param {string} productHandle - Handle do produto (URL slug)
   * @param {string} colorValue - Valor da cor para filtrar
   */
  async updateGalleryWithProductImages(productHandle, colorValue) {
    console.log('🔄 updateGalleryWithProductImages INICIADO');
    console.log('   Handle:', productHandle);
    console.log('   Cor:', colorValue);
    
    const gallery = document.querySelector('media-gallery');
    console.log('   Galeria encontrada:', !!gallery);
    
    if (!gallery) {
      console.warn('⚠️ Galeria não encontrada');
      return;
    }

    try {
      // Buscar dados do produto via API
      const url = `/products/${productHandle}.js`;
      console.log('📡 Fetching:', url);
      
      const response = await fetch(url);
      console.log('📡 Response status:', response.status);
      
      const productData = await response.json();
      
      console.log('📦 Produto carregado:', productData.title);
      console.log('📦 Total de imagens:', productData.images.length);
      
      // Normalizar cor para comparação
      const normalizedColor = this.normalizeString(colorValue);
      console.log('🎨 Cor normalizada:', normalizedColor);
      
      // Primeiro, vamos ver a estrutura das imagens
      console.log('🔍 Estrutura da primeira imagem:', productData.images[0]);
      
      // Filtrar imagens que correspondem à cor
      const matchingImages = productData.images.filter(img => {
        // A API retorna 'src' diretamente como string, não como objeto
        const imgSrc = img.src || img;
        if (!imgSrc) {
          console.warn('⚠️ Imagem sem src:', img);
          return false;
        }
        
        const filename = this.normalizeString(imgSrc.split('/').pop());
        const altText = this.normalizeString(img.alt || '');
        const matches = filename.includes(normalizedColor) || altText.includes(normalizedColor);
        
        console.log(`   Imagem: ${imgSrc.split('/').pop()}`);
        console.log(`   Filename normalizado: ${filename}`);
        console.log(`   Alt normalizado: ${altText}`);
        console.log(`   Match? ${matches}`);
        
        return matches;
      });
      
      console.log(`🖼️ Imagens encontradas para ${colorValue}:`, matchingImages.length);
      
      // Se nenhuma imagem corresponder, usar todas
      const imagesToShow = matchingImages.length > 0 ? matchingImages : productData.images;
      
      console.log('🎯 Imagens a serem mostradas:', imagesToShow.length);
      
      // Atualizar galeria
      this.replaceGalleryImages(imagesToShow);
      
    } catch (error) {
      console.error('❌ Erro ao carregar imagens do produto:', error);
      console.error('Stack:', error.stack);
    }
  }

  /**
   * Substitui as imagens da galeria principal
   * @param {Array} images - Array de imagens do Shopify
   */
  replaceGalleryImages(images) {
    console.log('🔄 replaceGalleryImages INICIADO com', images.length, 'imagens');
    
    const gallery = document.querySelector('media-gallery');
    if (!gallery) {
      console.warn('⚠️ Galeria não encontrada');
      return;
    }
    
    const mediaList = gallery.querySelector('.product__media-list');
    console.log('📋 Media list encontrada:', !!mediaList);
    
    if (!mediaList) {
      console.warn('⚠️ Lista de media não encontrada - tentando seletor alternativo');
      
      // Tentar seletor alternativo
      const slider = gallery.querySelector('slider-element .product__media-list');
      if (slider) {
        console.log('✅ Encontrado via slider-element');
        return this.replaceGalleryImagesInContainer(slider, images);
      }
      
      console.error('❌ Não foi possível encontrar container de imagens');
      return;
    }
    
    this.replaceGalleryImagesInContainer(mediaList, images);
  }

  replaceGalleryImagesInContainer(container, images) {
    console.log('🔧 Substituindo imagens no container');
    
    // Limpar galeria atual
    container.innerHTML = '';
    
    // Adicionar novas imagens
    images.forEach((img, index) => {
      // A API pode retornar img.src ou img diretamente como string
      const imgSrc = img.src || img;
      const imgAlt = img.alt || 'Imagem do produto';
      
      console.log(`   🖼️ Processando imagem ${index + 1}:`, imgSrc);
      
      const mediaItem = document.createElement('div');
      mediaItem.className = 'product__media card media flex w-full flex-auto relative overflow-hidden';
      mediaItem.innerHTML = `
        <img
          src="${imgSrc}"
          alt="${imgAlt}"
          loading="${index === 0 ? 'eager' : 'lazy'}"
          width="800"
          height="800"
        />
      `;
      container.appendChild(mediaItem);
      console.log(`   ✅ Adicionada: ${imgSrc.split('/').pop()}`);
    });
    
    console.log(`✅ Galeria atualizada com ${images.length} imagens`);
    
    // Atualizar thumbnails se existirem
    this.updateGalleryThumbnails(images);
  }

  /**
   * Atualiza thumbnails da galeria
   * @param {Array} images - Array de imagens do Shopify
   */
  updateGalleryThumbnails(images) {
    const gallery = document.querySelector('media-gallery');
    if (!gallery) return;
    
    const thumbnailList = gallery.querySelector('media-dots');
    if (!thumbnailList) {
      console.log('⏭️ Thumbnails não encontrados (opcional)');
      return;
    }
    
    console.log('🔄 Atualizando thumbnails...');
    
    // Limpar thumbnails
    thumbnailList.innerHTML = '';
    
    // Adicionar novos thumbnails
    images.forEach((img, index) => {
      const imgSrc = img.src || img;
      const imgAlt = img.alt || 'Miniatura';
      
      const thumb = document.createElement('button');
      thumb.type = 'button';
      thumb.className = 'media-dot';
      thumb.setAttribute('aria-label', `Visualizar imagem ${index + 1}`);
      thumb.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      thumb.innerHTML = `
        <img
          src="${imgSrc}"
          alt="${imgAlt}"
          width="80"
          height="80"
          loading="lazy"
        />
      `;
      thumbnailList.appendChild(thumb);
    });
    
    console.log(`✅ ${images.length} thumbnails atualizados`);
  }

  /**
   * Filtra imagens da galeria principal por cor selecionada
   * Verifica tanto o nome do arquivo quanto o alt text
   * @param {string} colorValue - Valor da cor (ex: "Azul Marinho", "Preto")
   */
  filterMainGalleryByColor(colorValue) {
    console.log('🎨 Filtrando galeria por cor:', colorValue);
    
    // Normalizar cor (handleize + downcase)
    const normalizedColor = this.normalizeString(colorValue);
    
    // Buscar galeria principal
    const gallery = document.querySelector('media-gallery');
    if (!gallery) {
      console.warn('⚠️ Galeria principal não encontrada');
      return;
    }
    
    // Buscar todos os media items
    const mediaItems = gallery.querySelectorAll('.product__media');
    let matchCount = 0;
    let totalItems = 0;
    
    mediaItems.forEach((item) => {
      totalItems++;
      
      // Buscar imagem dentro do media item
      const img = item.querySelector('img');
      if (!img) return;
      
      // Verificar filename (src)
      const filename = this.normalizeString(img.src.split('/').pop());
      
      // Verificar alt text
      const altText = this.normalizeString(img.alt || '');
      
      // Verificar se contém a cor
      const hasColor = filename.includes(normalizedColor) || altText.includes(normalizedColor);
      
      if (hasColor) {
        item.removeAttribute('hidden');
        item.style.display = '';
        matchCount++;
        console.log(`✅ Mostrando imagem: ${img.src.split('/').pop()}`);
      } else {
        item.setAttribute('hidden', 'true');
        item.style.display = 'none';
        console.log(`❌ Ocultando imagem: ${img.src.split('/').pop()}`);
      }
    });
    
    console.log(`🖼️ Filtro aplicado: ${matchCount}/${totalItems} imagens mostradas`);
    
    // Se nenhuma imagem corresponder, mostrar todas (fallback)
    if (matchCount === 0) {
      console.warn('⚠️ Nenhuma imagem encontrada para esta cor, mostrando todas');
      mediaItems.forEach((item) => {
        item.removeAttribute('hidden');
        item.style.display = '';
      });
    }
    
    // Atualizar thumbnails se existirem
    const thumbnails = gallery.querySelectorAll('media-dots button');
    let visibleIndex = 0;
    thumbnails.forEach((thumb, index) => {
      const mediaItem = mediaItems[index];
      if (mediaItem && !mediaItem.hasAttribute('hidden')) {
        thumb.removeAttribute('hidden');
        thumb.style.display = '';
        // Marcar primeiro visível como ativo
        if (visibleIndex === 0) {
          thumb.setAttribute('aria-selected', 'true');
          mediaItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        } else {
          thumb.setAttribute('aria-selected', 'false');
        }
        visibleIndex++;
      } else {
        thumb.setAttribute('hidden', 'true');
        thumb.style.display = 'none';
      }
    });
  }

  /**
   * Normaliza string para comparação (handleize + lowercase)
   * Remove acentos, espaços, converte para minúsculas
   * @param {string} str - String para normalizar
   * @returns {string} String normalizada
   */
  normalizeString(str) {
    if (!str) return '';
    
    return str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove acentos
      .replace(/[^a-z0-9]+/g, '-')      // Substitui caracteres especiais por hífen
      .replace(/^-+|-+$/g, '');         // Remove hífens do início/fim
  }

  formatMoney(cents) {
    // Usar o formatador de moeda do Shopify se disponível
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, window.themeVariables?.settings?.moneyFormat || '{{amount}}');
    }
    
    // Fallback simples
    return `R$ ${(cents / 100).toFixed(2).replace('.', ',')}`;
  }
}

// Inicializar quando o DOM estiver pronto
document.addEventListener('DOMContentLoaded', () => {
  // Encontrar todos os kit builders na página
  const containers = document.querySelectorAll('[data-kit-id]');
  
  containers.forEach((container) => {
    const kitId = container.dataset.kitId;
    const config = window.ProductKitBuilder[kitId];
    
    if (config) {
      new ProductKitBuilder(container, config);
      console.log(`✅ Kit Builder inicializado: ${kitId}`);
    } else {
      console.warn(`⚠️ Configuração não encontrada para kit: ${kitId}`);
    }
  });
});

